import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Download, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { downloadMedia, friendlyFileName } from '../lib/downloadMedia';
import { useAuth } from '../contexts/AuthContext';
import { shouldSuppressVideoJobCompletionToast } from '../lib/jobsApi';
import { notifyVideoReadyEmail } from '../lib/notifyVideoReady';
import {
  useGenerationToast,
  previewFromNotification,
  type GenerationPreview,
} from '../contexts/GenerationToastContext';
import type { Database } from '../types/database.types';

type Notification = Database['public']['Tables']['notifications']['Row'];

const TOAST_TTL_MS = 12000;

export default function GenerationCompletionToasts() {
  const { user } = useAuth();
  const { toasts, preview, pushCompletionToast, dismissToast, openPreview, closePreview } =
    useGenerationToast();

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`generation-toasts-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as Notification;
          if (row.type !== 'generation_completed') return;
          const next = previewFromNotification(row);
          if (!next) return;
          const meta =
            row.metadata && typeof row.metadata === 'object' && !Array.isArray(row.metadata)
              ? (row.metadata as Record<string, unknown>)
              : {};
          const jobId = typeof meta.job_id === 'string' ? meta.job_id : '';
          // After video completion: email if generation took > 2 min (server decides).
          if (jobId && next.mediaType === 'video') notifyVideoReadyEmail(jobId);
          if (jobId && shouldSuppressVideoJobCompletionToast(jobId)) return;
          pushCompletionToast({ ...next, id: `n-${row.id}` });
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, pushCompletionToast]);

  useEffect(() => {
    if (!toasts.length) return;
    const timers = toasts.map((t) =>
      window.setTimeout(() => dismissToast(t.id), Math.max(1000, TOAST_TTL_MS - (Date.now() - t.createdAt))),
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [toasts, dismissToast]);

  useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePreview();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview, closePreview]);

  const handleToastClick = (toastId: string, item: GenerationPreview) => {
    dismissToast(toastId);
    openPreview(item);
    if (item.notificationId) {
      void supabase.from('notifications').update({ is_read: true }).eq('id', item.notificationId);
    }
  };

  const isImage = (preview?.mediaType || '').toLowerCase().includes('image');

  return (
    <>
      <div className="generation-toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="generation-toast" role="status">
            <button
              type="button"
              className="generation-toast-body"
              onClick={() => handleToastClick(t.id, t)}
            >
              <span className="generation-toast-icon" aria-hidden>
                <CheckCircle2 size={18} />
              </span>
              <span className="generation-toast-copy">
                <strong>{t.title}</strong>
                <span>{t.message || 'Clique para visualizar'}</span>
              </span>
            </button>
            <button
              type="button"
              className="generation-toast-close"
              aria-label="Fechar"
              onClick={() => dismissToast(t.id)}
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>

      {preview && createPortal(
        <div
          className="generation-preview-overlay"
          onClick={closePreview}
          role="presentation"
        >
          <div
            className="generation-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-label={preview.title}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="generation-preview-header">
              <div>
                <h3>{preview.title}</h3>
                {preview.message && <p>{preview.message}</p>}
              </div>
              <span style={{ display: 'inline-flex', gap: 6 }}>
                <button
                  type="button"
                  className="generation-preview-close"
                  aria-label="Baixar"
                  title="Baixar"
                  onClick={() => void downloadMedia(preview.mediaUrl, friendlyFileName({
                    reference: preview.title,
                    url: preview.mediaUrl,
                    fallbackExt: isImage ? 'png' : 'mp4',
                  }))}
                >
                  <Download size={18} />
                </button>
                <button type="button" className="generation-preview-close" onClick={closePreview} aria-label="Fechar">
                  <X size={18} />
                </button>
              </span>
            </div>
            <div className="generation-preview-media">
              {isImage ? (
                <img src={preview.mediaUrl} alt="" />
              ) : (
                <video src={preview.mediaUrl} controls controlsList="nodownload" autoPlay playsInline />
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
